import { type FC, type ReactElement, useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import { useTheme } from '@mui/material/styles'

import { ESCAPE } from '@Constants/keyboard'
import BottomSheet from '@DS/components/components/foundations/shapes/bottomSheet/BottomSheet'
import {
  type AdditionalMessageButtonProps,
  type DescriptionMessage,
  type MediumSizeView,
} from '@DS/components/components/tooltip/additionalMessage/AdditionalMessage.types'
import Tooltip from '@DS/components/components/tooltip/Tooltip'
import { type TooltipPlacement } from '@DS/components/components/tooltip/Tooltip.types'
import Modal from '@DS/components/navigation/modal/Modal'
import { useIsInModal, useModalControlsIfIsInModal } from '@DS/components/navigation/modal/ModalContext'
import ModalPage from '@DS/components/navigation/modal/ModalPage'
import ScrollableContent from '@DS/components/navigation/modal/ScrollableContent'
import useEventListener, { getKeyboardEvent } from '@Hooks/useEventListener'
import { useIsMediumUp } from '@Hooks/useResponsive'

type UseAdditionalMessageReturnType = {
  close: VoidFunction
  toggle: VoidFunction
  buttonProps: AdditionalMessageButtonProps
  renderMessageView: (children: ReactElement) => ReactElement
}

type UseAdditionalMessageParams = {
  id: string
  iconAriaLabel?: string
  mediumSizeView?: MediumSizeView
  descriptionMessage: DescriptionMessage
  tooltipPlacement?: TooltipPlacement
}

const AdditionalMessageModalPage: FC<Pick<UseAdditionalMessageParams, 'descriptionMessage'>> = ({
  descriptionMessage: { content, title },
}) => {
  const { formatMessage } = useIntl()
  const defaultMessageTitle = formatMessage({ id: 'additional_message_button_label' })

  return (
    <ModalPage headerTitle={title || defaultMessageTitle}>
      <ScrollableContent>{content}</ScrollableContent>
    </ModalPage>
  )
}

export const useAdditionalMessage = ({
  id,
  iconAriaLabel,
  mediumSizeView,
  descriptionMessage,
  tooltipPlacement,
}: UseAdditionalMessageParams): UseAdditionalMessageReturnType => {
  const [isOpen, setIsOpen] = useState(false)
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const isMediumUp = useIsMediumUp(theme)
  const isInModal = useIsInModal()
  const { goToDynamicModalPage } = useModalControlsIfIsInModal()

  const descriptionId = `${id}-message`
  const buttonIconId = `${descriptionId}-button`
  const iconA11yLabel = iconAriaLabel || formatMessage({ id: 'additional_message_button_label' })

  const isInAModalMode = isInModal && (mediumSizeView === 'modal' || !isMediumUp)
  const isModalMode = mediumSizeView === 'modal'
  const isInResponsiveMode = !isMediumUp

  const open = useCallback(() => {
    setIsOpen(true)

    if (isInAModalMode) {
      goToDynamicModalPage(<AdditionalMessageModalPage descriptionMessage={descriptionMessage} />)
    }
  }, [descriptionMessage, goToDynamicModalPage, isInAModalMode])

  const close = useCallback(() => setIsOpen(false), [])

  const toggle = useCallback(() => {
    if (isOpen) {
      close()
    } else {
      open()
    }
  }, [close, isOpen, open])

  useEventListener('keydown', (event: Event) => {
    if (getKeyboardEvent(event)?.key === ESCAPE) {
      close()
    }
  })

  const buttonProps = {
    'aria-label': iconA11yLabel,
    id: buttonIconId,
    ...(!isInResponsiveMode && !isModalMode && { onBlur: close }),
  } as AdditionalMessageButtonProps

  const renderMessageView = (children: ReactElement) => {
    if (isInAModalMode) {
      return <>{children}</>
    }

    if (isInResponsiveMode) {
      return (
        <>
          {children}
          <BottomSheet ariaLabelledBy={buttonIconId} isOpen={isOpen} onClose={close} title={descriptionMessage.title}>
            {descriptionMessage.content}
          </BottomSheet>
        </>
      )
    }

    if (isModalMode) {
      return (
        <>
          {children}

          <Modal isOpen={isOpen} onClose={close} aria-labelledby={buttonIconId}>
            <AdditionalMessageModalPage descriptionMessage={descriptionMessage} />
          </Modal>
        </>
      )
    }

    return (
      <Tooltip
        id={buttonIconId}
        title={descriptionMessage.title}
        content={descriptionMessage.content}
        onClose={close}
        open={isOpen}
        placement={tooltipPlacement}
      >
        {children}
      </Tooltip>
    )
  }

  return {
    close,
    toggle,
    buttonProps,
    renderMessageView,
  }
}
